/**
 * @license
 * Copyright Paysafe 2026. All rights reserved.
 */

import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiEnvironment, Wallet } from '@paysafe/paysafe-wallet-saas-web/wallet';
import {
  WalletCheckoutPaymentResponse,
  WalletCheckoutSessionResponse
} from '@paysafe/paysafe-wallet-saas-web/wallet-checkout';
import { PsButtonModule } from '@paysafe-ui/components/button';
import { PsCardBorder, PsCardDisplayDensity, PsCardModule } from '@paysafe-ui/components/card';
import { PsListModule } from '@paysafe-ui/components/list';
import { PsProgressSpinnerComponent } from '@paysafe-ui/components/progress-spinner';
import { combineLatest, firstValueFrom, from, interval, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { FAQ_URL } from '../../constants/checkout-error.const';
import { CheckoutErrorType } from '../../core/enums/checkout-error-type.enum';
import { CheckoutSuccessType } from '../../core/enums/checkout-success-type.enum';
import { ScaDetails } from '../../models/payment/session-payment-response.model';
import { SuccessMetadata } from '../../models/ui/success-metadata.model';
import { AuthTokenService } from '../../services/auth-token/auth-token.service';
import { CheckoutService } from '../../services/checkout/checkout-service';
import { CheckoutErrorHandlerService } from '../../services/error-handler/checkout-error-handler.service';
import { UIStateService } from '../../services/ui-state/ui-state.service';
import { formatCurrencyAmount } from '../../utils/currency.util';
import { ScaModalComponent } from '../sca/sca-modal/sca-modal.component';

@Component({
  selector: 'app-wallet-preview',
  providers: [CheckoutService],
  imports: [CommonModule, PsButtonModule, PsCardModule, PsListModule, PsProgressSpinnerComponent, ScaModalComponent,
    TranslateModule],
  templateUrl: './checkout-preview.component.html',
  styleUrl: './checkout-preview.component.scss'
})
export class CheckoutPreviewComponent implements OnInit, OnDestroy {

  protected readonly PsCardBorder = PsCardBorder;
  protected readonly PsCardDisplayDensity = PsCardDisplayDensity;
  protected readonly formatCurrencyAmount = formatCurrencyAmount;

  // URL parameters
  sessionId: string | null = null;
  secureCode: string | null = null;
  b2cToken: string | null = null;

  createPaymentResponse: WalletCheckoutPaymentResponse | null = null;
  scaDetails: ScaDetails | null = null;
  isLoading = false;
  isCancelling = false;
  isPolling = false;
  isOrderIdCopied = false;
  private pollSub?: Subscription;
  private copyResetTimeoutId?: ReturnType<typeof setTimeout>;
  private readonly maxPollAttempts = 30;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly checkoutService: CheckoutService,
    private readonly uiStateService: UIStateService,
    private readonly destroyRef: DestroyRef,
    private readonly authTokenService: AuthTokenService,
    private readonly checkoutErrorHandler: CheckoutErrorHandlerService,
    private readonly translateService: TranslateService
  ) {
    // Dependency injection
  }

  ngOnInit(): void {
    combineLatest([
      this.route.queryParams,
      this.route.fragment
    ]).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([queryParams, fragment]) => {
          if (queryParams) {
            this.sessionId = queryParams.sessionId || null;
            this.secureCode = queryParams.secureCode || null;
          }

          if (fragment) {
            if (typeof fragment === 'string') {
              this.b2cToken = /token=([^&]+)/.exec(fragment)?.[1] ?? null;
            }
          }

          if (!this.sessionId || !this.secureCode || !this.b2cToken) {
            this.showTranslatedGeneralError('CHECKOUT.PREVIEW.REQUIRED_PARAMETERS_NOT_PROVIDED');
            return;
          }
          this.checkoutService.init({
            sessionId: this.sessionId,
            secureCode: this.secureCode
          });
          this.configureWebSdkTokens().then(() => {
            this.checkoutService.getSession()
              .then((session: WalletCheckoutSessionResponse) =>
                this.checkoutErrorHandler.storeReturnUrl(session.returnUrl));

            this.checkoutService.createPaymentPreview()
              .then((createPaymentResponse: WalletCheckoutPaymentResponse) => {
                if (this.validatePaymentStatus(createPaymentResponse)) {
                  this.createPaymentResponse = createPaymentResponse;
                } else {
                  this.checkoutErrorHandler.handleCancellation();
                }
              })
              .catch(error => this.checkoutErrorHandler.handleError(error));
          });
        }, error: () => {
          this.showTranslatedGeneralError('CHECKOUT.PREVIEW.ERROR_GETTING_REQUIRED_PARAMETERS');
        }
    });
  }

  ngOnDestroy(): void {
    if (this.copyResetTimeoutId) {
      clearTimeout(this.copyResetTimeoutId);
      this.copyResetTimeoutId = undefined;
    }
  }

  get hasSufficientBalance(): boolean {
    if (!this.createPaymentResponse) {
      return false;
    }
    const currentBalance = this.createPaymentResponse.payment?.currentBalance;
    const balanceAfterPayment = this.createPaymentResponse.payment?.balanceAfterPayment;
    return currentBalance > 0n && balanceAfterPayment >= 0n;
  }

  get amount(): number {
    return this.createPaymentResponse?.amount;
  }

  get feeAmount(): number {
    return this.createPaymentResponse?.payment?.fee;
  }

  get totalAmount(): number {
    return this.createPaymentResponse?.payment?.total;
  }

  get currentBalanceAmount(): number {
    return this.createPaymentResponse?.payment?.currentBalance;
  }

  onCopyOrderId(): void {
    const orderId = this.createPaymentResponse?.merchantRefNum;
    if (!orderId || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
      return;
    }
    navigator.clipboard.writeText(orderId).then(() => {
      this.isOrderIdCopied = true;
      if (this.copyResetTimeoutId) {
        clearTimeout(this.copyResetTimeoutId);
      }
      this.copyResetTimeoutId = setTimeout(() => {
        this.isOrderIdCopied = false;
      }, 1500);
    }).catch(() => { /* clipboard permission denied or unavailable */ });
  }

  private validatePaymentStatus(createPaymentResponse: WalletCheckoutPaymentResponse): boolean {
    if (!createPaymentResponse) {
      return false;
    }
    const payment = createPaymentResponse.payment;
    if (!payment) {
      return false;
    }
    return payment.status === 'PREVIEW' && payment.nextStatus
      && payment.nextStatus.length > 0 && payment.nextStatus[0] === 'PENDING';
  }

  private showTranslatedGeneralError(translationKey: string): void {
    this.translateService.get(translationKey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(subtitle => {
        this.uiStateService.showError(CheckoutErrorType.GENERAL, { subtitle, secondaryActionUrl: FAQ_URL });
      });
  }

  onConfirm(): void {
    if (this.isLoading || this.isCancelling || !this.hasSufficientBalance) {
      return;
    }

    this.isLoading = true;
    void this.runConfirmPaymentFlow();
  }

  private async runConfirmPaymentFlow(): Promise<void> {
    try {
      const pendingResponse = await this.checkoutService.updatePaymentStatus('PENDING');
      if (!this.isValidPendingResponse(pendingResponse)) {
        this.isLoading = false;
        this.handleUnexpectedState();
        return;
      }
      const processingResponse = await this.checkoutService.updatePaymentStatus('PROCESSING');
      this.handleProcessingResponse(processingResponse);
    } catch (error) {
      this.isLoading = false;
      this.checkoutErrorHandler.handleError(error);
    }
  }

  onCancel(): void {
    if (this.isLoading || this.isCancelling) {
      return;
    }
    this.stopPolling();
    this.isCancelling = true;
    this.checkoutService.updatePaymentStatus('CANCELLED')
      .then((response: WalletCheckoutPaymentResponse) => {
        this.isCancelling = false;
        this.showResultScreen(response);
      })
      .catch(error => {
        this.isCancelling = false;
        this.checkoutErrorHandler.handleError(error);
      });
  }

  onScaVerified(): void {
    if (!this.scaDetails) {
      return;
    }
    const scaPayload = {
      eventId: this.scaDetails.eventId,
      walletOperationId: this.scaDetails.walletOperationId
    };
    this.scaDetails = null;
    this.resumeAfterSca(scaPayload);
  }

  onScaError(): void {
    this.checkoutService.updatePaymentStatus('PROCESSING')
      .then((response: WalletCheckoutPaymentResponse) => {
        if (response.scaDetails) {
          this.scaDetails = response.scaDetails;
        }
      })
      .catch(error => {
        this.checkoutErrorHandler.handleError(error);
      });
  }

  onScaClosed(): void {
    this.scaDetails = null;
    this.checkoutErrorHandler.handleCancellation();
  }

  private resumeAfterSca(sca: { eventId: string; walletOperationId: string }): void {
    this.isLoading = true;
    this.checkoutService.updatePaymentStatus('PROCESSING', sca)
      .then((response: WalletCheckoutPaymentResponse) => {
        this.handleProcessingResponse(response);
      })
      .catch(error => {
        this.isLoading = false;
        this.checkoutErrorHandler.handleError(error);
    });
  }

  private handleProcessingResponse(response: WalletCheckoutPaymentResponse): void {
    const paymentStatus = response.payment?.status;

    if (response.scaDetails) {
      this.isLoading = false;
      this.scaDetails = response.scaDetails;
      return;
    }

    if (paymentStatus === 'PROCESSING' && response.status === 'PAYMENT_BOUND') {
      this.isLoading = false;
      this.startPollingForCompletion();
      return;
    }

    if (paymentStatus === 'COMPLETED'
      || paymentStatus === 'FAILED'
      || paymentStatus === 'CANCELLED') {
      this.showResultScreen(response);
      return;
    }

    this.isLoading = false;
    this.handleUnexpectedState();
  }

  private startPollingForCompletion(): void {
    if (this.isPolling) {
      return;
    }
    this.isPolling = true;
    this.pollSub?.unsubscribe();
    let attempts = 0;
    this.pollSub = interval(2000)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => from(this.checkoutService.getSession()))
      )
      .subscribe({
        next: (response: WalletCheckoutSessionResponse) => {
          attempts += 1;
          const status = response.payment?.status;
          if (status === 'COMPLETED'
            || status === 'FAILED'
            || status === 'CANCELLED') {
            this.stopPolling();
            this.showResultScreen(response);
            return;
          }
          if (attempts >= this.maxPollAttempts) {
            this.stopPolling();
            this.uiStateService.showError(CheckoutErrorType.PAYMENT_TIMED_OUT);
          }
        },
        error: (error): void => {
          this.stopPolling();
          this.checkoutErrorHandler.handleError(error);
        }
      });
  }

  private stopPolling(): void {
    this.isPolling = false;
    this.pollSub?.unsubscribe();
  }

  private showResultScreen(
    response: WalletCheckoutPaymentResponse | WalletCheckoutSessionResponse
  ): void {
    const paymentStatus = response.payment?.status;
    if (paymentStatus === 'COMPLETED') {
      this.checkoutService.getSession()
        .then((sessionResponse: WalletCheckoutSessionResponse) => {
          const successMetadata: SuccessMetadata = {
            redirectPath: sessionResponse.returnUrl
          };
          this.uiStateService.showSuccess(CheckoutSuccessType.PAYMENT_COMPLETED, successMetadata);
        })
        .catch(() => {
          // show success screen without setting the redirect path:
          this.uiStateService.showSuccess(CheckoutSuccessType.PAYMENT_COMPLETED);
        });
      return;
    }
    if (paymentStatus === 'CANCELLED') {
      this.uiStateService.showError(CheckoutErrorType.PAYMENT_CANCELLED);
      return;
    }
    if (paymentStatus === 'FAILED') {
      this.uiStateService.showError(CheckoutErrorType.TRANSACTION_FAILED, { secondaryActionUrl: FAQ_URL });
      return;
    }
    this.handleUnexpectedState();
  }

  private handleUnexpectedState(): void {
    this.checkoutErrorHandler.handleGenericError();
  }

  private isValidPendingResponse(response: WalletCheckoutPaymentResponse): boolean {
    const status = response.payment?.status;
    const nextStatus = response.payment?.nextStatus ?? [];
    return response.status === 'PAYMENT_BOUND'
      && status === 'PENDING'
      && nextStatus.some(s => s === 'PROCESSING');
  }

  // Configure required WEB SDK tokens
  async configureWebSdkTokens(): Promise<void> {
    const wallet = Wallet.getInstance();
    const apiEnvironment = ApiEnvironment.create(environment.WALLET_API_URI);
    const configToken = await firstValueFrom(this.authTokenService.fetchConfigToken(this.b2cToken));
    await wallet.configure({
      apiEnvironment,
      configToken
    });
    await wallet.authenticate({
      accessToken: this.b2cToken
    });
  }
}
